import useRequest from "../../../api/useRequest";
import OutsideAlerter from "../../../hooks/useOutsideAlerter";
import { LinkedInIntegrationsRequestFactory } from "../../../api/requestFactory";
import { useContext } from "react";
import { NotificationContext } from "../../../context/notification-context";
import { ERROR_CODES } from "../../../api/errorCodes";

export const MIGRATE_ERROR_INFO = {
  [ERROR_CODES.LINKEDIN_MIGRATE_ERROR_ACCOUNT]: {
    header:
      "You're about to move your LinkedIn profile to a different Supergrow account",
    info: "This change will disconnect your LinkedIn profile from the other account and connect it to this account.",
    warning: "Any scheduled posts from your old account will be unscheduled.",
    stayButton: "Stay with Current Account",
  },
  [ERROR_CODES.LINKEDIN_MIGRATE_ERROR_WORKSPACE]: {
    header:
      "You're about to move your LinkedIn profile to a different Workspace",
    info: "This change will disconnect your LinkedIn profile from workspace and connect it to this workspace.",
    warning: "Any scheduled posts from your old workspace will be unscheduled.",
    stayButton: "Stay with Current Workspace",
  },
};

export default function LinkedInMigrateAccount({
  onClose,
  name,
  avatarUrl,
  tempToken,
  code,
}) {
  const { requestMaker } = useRequest();
  const { showNotification } = useContext(NotificationContext);

  const modalInfo = MIGRATE_ERROR_INFO[code];

  const onSwitchProfileClick = async () => {
    try {
      const res = await requestMaker(
        LinkedInIntegrationsRequestFactory.createMigrateLinkedInAccountIntegrationRequest(
          tempToken
        )
      );

      showNotification(
        {
          title: "Success",
          content: res?.message,
        },
        "success"
      );
      window.location.reload();
    } catch (err) {
      console.error(err);
      showNotification(
        {
          title: "Error",
          content: "Something went wrong. Try again",
        },
        "error"
      );
    }
  };

  return (
    <div className="fixed inset-0 z-40 grid w-screen h-screen p-4 place-items-center bg-gray-700/75 sm:p-6 lg:p-8">
      <OutsideAlerter callback={onClose}>
        <div className="relative w-full max-w-lg mx-auto overflow-hidden bg-white shadow-xl rounded-xl">
          <div className="flex flex-col gap-6 px-4 py-6 sm:py-8 sm:px-6 overflow-y-auto max-h-[80vh] lg:max-h-[80vh]">
            <div className="flex flex-col gap-3 items-center justify-center">
              <div className="relative">
                <img
                  src={avatarUrl}
                  alt={name}
                  className="h-24 w-24 rounded-full"
                />
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 36 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="absolute bottom-0 right-0"
                >
                  <rect
                    x="1"
                    y="1"
                    width="34"
                    height="34"
                    rx="17"
                    fill="#1052B8"
                  />
                  <rect
                    x="1"
                    y="1"
                    width="34"
                    height="34"
                    rx="17"
                    stroke="white"
                    strokeWidth="2"
                  />
                  <path
                    d="M12.1525 13.9975C13.1595 13.9975 13.9758 13.1812 13.9758 12.1742C13.9758 11.1672 13.1595 10.3508 12.1525 10.3508C11.1455 10.3508 10.3292 11.1672 10.3292 12.1742C10.3292 13.1812 11.1455 13.9975 12.1525 13.9975Z"
                    fill="white"
                  />
                  <path
                    d="M15.6975 15.3792V25.495H18.8383V20.4925C18.8383 19.1725 19.0867 17.8942 20.7233 17.8942C22.3375 17.8942 22.3575 19.4033 22.3575 20.5758V25.4958H25.5V19.9483C25.5 17.2233 24.9133 15.1292 21.7283 15.1292C20.1992 15.1292 19.1742 15.9683 18.755 16.7625H18.7125V15.3792H15.6975ZM10.5792 15.3792H13.725V25.495H10.5792V15.3792Z"
                    fill="white"
                  />
                </svg>
              </div>
              <p className="font-semibold text-gray-900 text-lg">{name}</p>
            </div>

            <div className="flex flex-col gap-1 items-center">
              <p className="text-center text-xl font-semibold text-gray-950 w-11/12">
                {modalInfo?.header}
              </p>
              <p className="text-center text-gray-600 w-11/12">
                {modalInfo?.info}
              </p>
            </div>

            <div className="flex gap-2 bg-warning-50 px-3 py-2.5 rounded-10 border border-warning-200">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8.257 3.09898C9.022 1.73898 10.979 1.73898 11.743 3.09898L17.323 13.019C18.073 14.353 17.11 15.999 15.581 15.999H4.42C2.89 15.999 1.927 14.353 2.677 13.019L8.257 3.09898ZM11 13C11 13.2652 10.8946 13.5196 10.7071 13.7071C10.5196 13.8946 10.2652 14 10 14C9.73478 14 9.48043 13.8946 9.29289 13.7071C9.10536 13.5196 9 13.2652 9 13C9 12.7348 9.10536 12.4804 9.29289 12.2929C9.48043 12.1053 9.73478 12 10 12C10.2652 12 10.5196 12.1053 10.7071 12.2929C10.8946 12.4804 11 12.7348 11 13ZM10 4.99998C9.73478 4.99998 9.48043 5.10534 9.29289 5.29287C9.10536 5.48041 9 5.73476 9 5.99998V8.99998C9 9.2652 9.10536 9.51955 9.29289 9.70709C9.48043 9.89462 9.73478 9.99998 10 9.99998C10.2652 9.99998 10.5196 9.89462 10.7071 9.70709C10.8946 9.51955 11 9.2652 11 8.99998V5.99998C11 5.73476 10.8946 5.48041 10.7071 5.29287C10.5196 5.10534 10.2652 4.99998 10 4.99998Z"
                  fill="#F79009"
                />
              </svg>

              <p className="text-sm text-warning-800">{modalInfo?.warning}</p>
            </div>

            <div className="flex flex-col gap-4 sm:justify-center sm:items-center sm:flex-row">
              <button
                onClick={onClose}
                type="button"
                className="inline-flex items-center justify-center gap-2 transition-all duration-150 rounded-full bg-white px-3 py-2 sm:px-4 sm:py-2.5 text-sm font-semibold text-gray-700 shadow-xs ring-1 hover:text-gray-900 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                {modalInfo?.stayButton}
              </button>

              <button
                type="button"
                onClick={onSwitchProfileClick}
                className="inline-flex items-center justify-center gap-2 transition-all duration-150 rounded-full px-3 py-2 sm:px-4 sm:py-2.5 text-sm font-semibold hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 bg-blue-500 text-white"
              >
                Yes, Switch Profile
              </button>
            </div>

            <p className="max-w-sm mx-auto text-xs text-center font-medium text-gray-600">
              Need help?{" "}
              <a
                target="_blank"
                rel="noreferrer"
                href="https://help.supergrow.ai/"
                className="underline"
              >
                Contact our support team.
              </a>
            </p>
          </div>
        </div>
      </OutsideAlerter>
    </div>
  );
}
